import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { API_URL } from '../config/api';
import './PaidAds.css';

interface Campaign {
  campaignId: string;
  name: string;
  status: string;
  secondaryStatus: string;
  objective: string;
  budget: number;
  budgetMode: string;
}

interface AdGroup {
  adgroupId: string;
  name: string;
  status: string;
  secondaryStatus: string;
  budget: number;
  optimizationGoal: string;
  bidPrice: number;
}

interface Ad {
  adId: string;
  name: string;
  status: string;
  secondaryStatus: string;
}

interface TikTokAdsData {
  campaigns: Campaign[];
  adGroups: AdGroup[];
  ads: Ad[];
}

const PaidAds: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'tiktok';

  const [tiktokData, setTiktokData] = useState<TikTokAdsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setActiveTab = (tab: string) => {
    setSearchParams({ tab });
  };

  useEffect(() => {
    if (activeTab === 'tiktok') {
      fetchTikTokAds();
    }
  }, [activeTab]);

  const fetchTikTokAds = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/tiktok-ads/status`);
      const data = await response.json();

      if (data.success) {
        setTiktokData(data.data);
      } else {
        setError(data.message || 'Failed to fetch TikTok ads');
      }
    } catch (err) {
      setError('Failed to connect to server');
      console.error('Error fetching TikTok ads:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string, secondaryStatus: string) => {
    const statusLower = (secondaryStatus || status).toLowerCase();

    if (statusLower.includes('enable') || statusLower.includes('active')) {
      return <span className="status-badge active">Active</span>;
    } else if (statusLower.includes('audit') || statusLower.includes('review')) {
      return <span className="status-badge review">In Review</span>;
    } else if (statusLower.includes('disable') || statusLower.includes('paused')) {
      return <span className="status-badge paused">Paused</span>;
    } else if (statusLower.includes('reject') || statusLower.includes('fail')) {
      return <span className="status-badge rejected">Rejected</span>;
    }
    return <span className="status-badge paused">{status}</span>;
  };

  const renderTikTokTab = () => {
    if (loading) {
      return (
        <div className="paid-ads-loading">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Loading TikTok Ads...
        </div>
      );
    }

    if (error) {
      return (
        <div className="paid-ads-error">
          {error}
          <button onClick={fetchTikTokAds}>Retry</button>
        </div>
      );
    }

    if (!tiktokData) {
      return <div className="paid-ads-empty">No data available</div>;
    }

    return (
      <div>
        {/* Campaigns */}
        <div className="paid-ads-card">
          <div className="paid-ads-card-header">
            <h3>Campaigns ({tiktokData.campaigns.length})</h3>
          </div>
          <table className="paid-ads-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Status</th>
                <th>Objective</th>
                <th>Budget</th>
              </tr>
            </thead>
            <tbody>
              {tiktokData.campaigns.map((campaign) => (
                <tr key={campaign.campaignId}>
                  <td>
                    <div className="name-cell">{campaign.name}</div>
                    <div className="id-cell">ID: {campaign.campaignId}</div>
                  </td>
                  <td>{getStatusBadge(campaign.status, campaign.secondaryStatus)}</td>
                  <td>{campaign.objective}</td>
                  <td>${campaign.budget}/{campaign.budgetMode === 'BUDGET_MODE_DAY' ? 'day' : 'total'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Ad Groups */}
        <div className="paid-ads-card">
          <div className="paid-ads-card-header">
            <h3>Ad Groups ({tiktokData.adGroups.length})</h3>
          </div>
          <table className="paid-ads-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Status</th>
                <th>Optimization</th>
                <th>Bid</th>
                <th>Budget</th>
              </tr>
            </thead>
            <tbody>
              {tiktokData.adGroups.map((adGroup) => (
                <tr key={adGroup.adgroupId}>
                  <td>
                    <div className="name-cell">{adGroup.name}</div>
                    <div className="id-cell">ID: {adGroup.adgroupId}</div>
                  </td>
                  <td>{getStatusBadge(adGroup.status, adGroup.secondaryStatus)}</td>
                  <td>{adGroup.optimizationGoal}</td>
                  <td>${adGroup.bidPrice}/click</td>
                  <td>${adGroup.budget}/day</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Ads */}
        <div className="paid-ads-card">
          <div className="paid-ads-card-header">
            <h3>Ads ({tiktokData.ads.length})</h3>
          </div>
          <table className="paid-ads-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Status</th>
                <th>Ad ID</th>
              </tr>
            </thead>
            <tbody>
              {tiktokData.ads.map((ad) => (
                <tr key={ad.adId}>
                  <td className="name-cell">{ad.name}</td>
                  <td>{getStatusBadge(ad.status, ad.secondaryStatus)}</td>
                  <td className="id-cell">{ad.adId}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderMetaTab = () => {
    return (
      <div className="paid-ads-meta-card">
        <div className="emoji">📣</div>
        <h3>Meta Ads Manager</h3>
        <p>Manage your Facebook and Instagram ads</p>
        <a
          href="https://adsmanager.facebook.com/adsmanager/manage/campaigns?act=963309378283661&nav_source=no_referrer"
          target="_blank"
          rel="noopener noreferrer"
          className="paid-ads-manage-btn"
        >
          Manage
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
        </a>
      </div>
    );
  };

  return (
    <div className="paid-ads-container">
      <div className="paid-ads-content">
        {/* Header */}
        <div className="paid-ads-header">
          <h1>Paid Ads</h1>
          <p>Manage your advertising campaigns across platforms</p>
        </div>

        {/* Tabs */}
        <div className="paid-ads-tabs">
          <button
            onClick={() => setActiveTab('tiktok')}
            className={`paid-ads-tab ${activeTab === 'tiktok' ? 'active' : ''}`}
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
            </svg>
            TikTok
          </button>
          <button
            onClick={() => setActiveTab('meta')}
            className={`paid-ads-tab ${activeTab === 'meta' ? 'active' : ''}`}
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2.04c-5.5 0-10 4.49-10 10.02 0 5 3.66 9.15 8.44 9.9v-7H7.9v-2.9h2.54V9.85c0-2.51 1.49-3.89 3.78-3.89 1.09 0 2.23.19 2.23.19v2.47h-1.26c-1.24 0-1.63.77-1.63 1.56v1.88h2.78l-.45 2.9h-2.33v7a10 10 0 008.44-9.9c0-5.53-4.5-10.02-10-10.02z"/>
            </svg>
            Meta
          </button>
        </div>

        {/* Action Buttons */}
        {activeTab === 'tiktok' && (
          <div className="paid-ads-actions">
            <a
              href="https://ads.tiktok.com/i18n/manage/creative?aadvid=7514993698887909393&is_refresh_page=true"
              target="_blank"
              rel="noopener noreferrer"
              className="paid-ads-manage-btn"
            >
              Manage
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
            <button
              onClick={fetchTikTokAds}
              disabled={loading}
              className={`paid-ads-refresh-btn ${loading ? 'loading' : ''}`}
            >
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
          </div>
        )}

        {/* Content */}
        {activeTab === 'tiktok' ? renderTikTokTab() : renderMetaTab()}
      </div>
    </div>
  );
};

export default PaidAds;
